import { useState, FC } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import VendorManagementGrid from 'src/components/Grids/VendorManagementGrid';
import TextField from 'src/components/InputFields/TextField';
import { useFormik } from 'formik';
import MobileField from 'src/components/InputFields/MobileField';
import {
  apiUrls,
  dialogTitles,
  dialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import MainLayout from 'src/pages/private';
import { vendorValidationSchema } from 'src/utils/validationScheme.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { vendorInitialValues } from 'src/utils/initialValues.utils';
import SearchBar from 'src/components/InputFields/Searchbar';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
} from 'src/components/StyledComponents/StyledPageComponents';
import { calculateDialogHeightByErrorCount } from 'src/utils/dialog.utils';

const VendorManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSnackbar } = StyledSnackbar();

  const [open, setOpen] = useState(false);
  const [vendorAdded, setVendorAdded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const formik = useFormik({
    initialValues: vendorInitialValues,
    validationSchema: vendorValidationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const getDialogHeight = () =>
    calculateDialogHeightByErrorCount(formik, 800, 10);

  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      const response = await axiosTokenInstance.post(apiUrls.ADD_VENDOR, {
        organizationName: values.organizationName,
        address: values.address,
        contactPersonName: values.contactPersonName,
        email: values.email,
        contactNo: `+${values.countryCode} ${values.phoneNumber}`,
      });

      showSnackbar(response.data.message, 'success');
      setVendorAdded(!vendorAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <MainLayout
      title="Vendor Management"
      searchBar={
        <SearchBar
          width="280px"
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search vendors by name or email"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="145px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Vendor
        </PrimaryActionButton>
      }
    >
      <VendorManagementGrid state={vendorAdded} searchTerm={searchTerm} />

      <Box display="flex">
        <Box display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title={dialogTitles.ADD_VENDOR}
            subtitle={dialogSubtitles.ADD_VENDOR}
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
            formBottomMargin="40px"
          >
            <TextField
              width="480px"
              id="organizationName"
              label="Organization"
              formikProps={formik}
            />

            <TextArea
              id="address"
              label="Address"
              formikProps={formik}
              rows={3}
              overflowY="auto !important"
            />

            <TextField
              width="480px"
              id="contactPersonName"
              label="Point of Contact"
              formikProps={formik}
            />
            <MobileField
              width="480px"
              id="contactNo"
              label="Contact Number"
              formikProps={formik}
            />
            <TextField
              width="480px"
              id="email"
              label="Email"
              type="email"
              formikProps={formik}
            />
          </CustomDialog>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default VendorManagement;
